import {Component, forwardRef, Inject, OnInit, ViewChild} from '@angular/core';
import {Location} from '@angular/common';
import * as _ from 'lodash';
import {EditorService, provideParent, StateMap, UnitOfWork, UserManager } from '../../services/common';
import {EditPropParent, PageState} from '../../controls/common';

import {ExpertPanelSummary, Material, MaterialExpertPanelSummary, MaterialMonograph} from '../../entities/EntityModels';

import {Observable, Subject} from 'rxjs';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {EditManager} from '../../utils/edit-manager';
import {MaterialFileEditorComponent} from './material-file-editor.component';
import {SelectableEntity} from '../../entities/projections/SelectableEntity';
import {MaterialWorkflowService} from '../../services/material-workflow-service';
import {WorkflowEntityState} from '../../services/workflow-state';
import {PublicationData, PublicationsComponent} from './publications.component';

export enum MaterialFileAction { NONE, ADD, DELETE, EDIT}
export enum MaterialFileType { NONE, EXPERTPANELSUMMARY, FRAGRANCEMATERIALREVIEW, MONOGRAPH}

export class MaterialFileData {
    key: number;
    materialId: number;
    fileLabel: string;
    fileName: string;
    fileType: MaterialFileType;
    fileAction: MaterialFileAction;
    processResultMsg: string;
    fileData: any;

    constructor(key: number, materialId: number, fileLabel: string, fileName: string, fileType: MaterialFileType, fileAction: MaterialFileAction) {
        this.key        = key;
        this.materialId = materialId;
        this.fileLabel  = fileLabel;
        this.fileName   = fileName;
        this.fileType   = fileType;
        this.fileAction = fileAction;
    }

    public materialFileAction(): string {
        if (this.fileAction == MaterialFileAction.ADD) {
            return 'File will be Added';
        } else if (this.fileAction == MaterialFileAction.DELETE) {
            return 'File will be Removed';
        } else if (this.fileAction = MaterialFileAction.EDIT) {
            return 'File will be Renamed';
        }

        return '';
    }

    public materialFileType(): string {
        if (this.fileType == MaterialFileType.EXPERTPANELSUMMARY) {
            return 'Expert Panel Summary';
        } else if (this.fileType == MaterialFileType.FRAGRANCEMATERIALREVIEW) {
            return 'Fragrance Material Review';
        } else if (this.fileType = MaterialFileType.MONOGRAPH) {
            return 'Monograph';
        }

        return '';
    }
}

export class MaterialFileEditData {
    edmMaterialMonograph: EditManager<MaterialMonograph>;
    editMaterialFileData: MaterialFileData;
    hasFileChanges: boolean;    // set when a file is uploaded
    inSubEditor: boolean;
    material: Material;
    nextFileName: string;

    constructor() {}
}

@Component({
    selector: 'material-file-manager',
    templateUrl: './material-file-manager.html',
    providers: [provideParent(MaterialFileManagerComponent, EditPropParent)]
})
export class MaterialFileManagerComponent implements OnInit, EditPropParent {
    @ViewChild(MaterialFileEditorComponent) _materialFileEditorComponent: MaterialFileEditorComponent;

    entity: MaterialMonograph;
    _pageState = new PageState('Material File Manager');
    _fmr: MaterialExpertPanelSummary;
    _summaries: ExpertPanelSummary[];

    // file download links
    _documentManagementUrl: string;
    _hasMonographFiles = false;
    _isLoading = false;
    _validationMessage = '';

    _materialFilesEx: SelectableEntity<MaterialFileData>[] = [];

    _data: MaterialFileEditData     = new MaterialFileEditData();
    _parentData: PublicationData    = new PublicationData();

    _materialWorkflowService: MaterialWorkflowService;

    constructor(public _uow: UnitOfWork, public _userManager: UserManager, public _stateMap: StateMap, public _editorService: EditorService,
                @Inject(forwardRef(() => PublicationsComponent)) public _parent: PublicationsComponent,
                public _location: Location, private _httpClient: HttpClient, materialWorkflowService: MaterialWorkflowService) {

        this._parentData = _parent._data;
        this._materialWorkflowService = materialWorkflowService;
    }

    ngOnInit() {
        this._data.material = this._stateMap.currentMaterial;
        this._data.hasFileChanges = false;
        this._data.inSubEditor = false;

        this._pageState.isLoading = true;

        this._data.edmMaterialMonograph = this._editorService.createEditManager<MaterialMonograph>(this, 'Material Monograph');

        this._uow.materialExpertPanelSummaryRepository.whereWithParams({materialId: this._data.material.materialId}).then(r => {
            this._fmr = _.find(r, m => m.review != null);
            const summaries = r.map(item => item.expertPanelSummary);
            this._summaries = summaries.filter(s => s.narrativeLink != null && s.narrativeLink.trim() != '');
            this._hasMonographFiles = (this._data.material.materialMonographs != null && this._data.material.materialMonographs.length > 0);

            this.prepareFileDisplay();

            this._pageState.isLoaded = (summaries.length > 0) || (this._fmr != null) || this._hasMonographFiles;
        });

        this._documentManagementUrl = this._stateMap.documentManagementBaseUrl + '/RIFMDownloadableFile';
        this._stateMap.documentManagementUrl = this._documentManagementUrl;
    }

    prepareFileDisplay() {
        this._materialFilesEx = [];

        if (this._hasMonographFiles) {
            const mfiles = _.sortBy(_.clone(this._data.material.materialMonographs), l => l.fileLabel);
            mfiles.forEach(element => {
                this.recordMaterialFile(element.materialMonographId, element.fileLabel, element.fileName, MaterialFileType.MONOGRAPH, MaterialFileAction.NONE, '#FFFFFF');
            });
        }

        if (this._fmr) {
            this.recordMaterialFile(this._fmr.expertPanelID, '', this._fmr.review, MaterialFileType.FRAGRANCEMATERIALREVIEW, MaterialFileAction.NONE, '#F0F0F0');
        }

        if (this._summaries && this._summaries.length > 0) {
            _.clone(this._summaries).forEach(element => {
                this.recordMaterialFile(element.expertPanelID, element.narrativeTitle, element.narrativeLink, MaterialFileType.EXPERTPANELSUMMARY, MaterialFileAction.NONE, '#F0F0F0');
            });
        }

        this._materialFilesEx = _.orderBy(this._materialFilesEx, ['fileType', 'fileName'], ['asc', 'asc']);
    }

    public get editMaterialFileData() {
        return this._data.editMaterialFileData;
    }

    public set editMaterialFileData(value: MaterialFileData ) {
        this._data.editMaterialFileData = value;
    }

    canAdd() {
        if (this._data.inSubEditor) { return false; }

        if (this._data.hasFileChanges) { return false; }

        if (this._data.edmMaterialMonograph.editing && this._uow.hasChanges()) {
            return false;
        }

        return true;
    }

    get canApply(): boolean {
        if (!this._data.inSubEditor) { return false; }

        if (this._data.editMaterialFileData.fileAction = MaterialFileAction.ADD) {
            if (!this._data.hasFileChanges) {   // this is set to true when a file is uploaded
                return false;
            }
        }

        if (this._data.edmMaterialMonograph.editing && this._uow.hasChanges()) {
            return (!this._data.edmMaterialMonograph.currentEntity.entityAspect.hasValidationErrors);
        }
    }

    get canCancel(): boolean {
        return (this._data.inSubEditor);
    }

    get canConfirmRemove(): boolean {
        if (!this._data.inSubEditor) { return false; }
        return (this._data.editMaterialFileData.fileAction == MaterialFileAction.DELETE);
    }

    get canSave(): boolean {
        if (!this._data.inSubEditor) { return false; }

        // file deletions are handled by the "Confirm Removal" button
        if (this._data.editMaterialFileData.fileAction == MaterialFileAction.DELETE) {
            return false;
        }

        if (this._data.editMaterialFileData.fileAction = MaterialFileAction.ADD) {
            if (!this._data.hasFileChanges) {   // this is set to true when a file is uploaded
                return false;
            }
        }

        if (this._data.edmMaterialMonograph.editing && this._uow.hasChanges()) {
            return (!this._data.edmMaterialMonograph.currentEntity.entityAspect.hasValidationErrors);
        }
    }

    canShowBack() {
        return (!this._data.inSubEditor);
    }

    canShowRemove(mf: MaterialFileData) {
        return (mf.fileType == MaterialFileType.MONOGRAPH);
    }

    getError(propName: string) {
        return this._data.material.getErrorFor(propName);
    }

    hasFileData() {
        return (this._materialFilesEx != null && this._materialFilesEx.length > 0);
    }

    nextMonographFileName(): string {
        const monographNum = (this._data.material.monographNumber == null) ? '' : '_' + this._data.material.monographNumber;
        if (this._data.material.materialMonographs == null || this._data.material.materialMonographs.length == 0) {
            return this._data.editMaterialFileData.materialId + monographNum;
        }

        let maxSeq = 0;
        const regEx = new RegExp('(.*_(\d+)$)', 'i');
        this._data.material.materialMonographs.forEach(mf => {
            if (!mf.entityAspect.entityState.isAdded()) {
                const fileData = mf.fileName.split('.');

                // file name format for new monograph files is: materialId_monographnumber_seqnumber
                const fname     = fileData[0];
                const uscore    = fname.charAt(fname.length - 2);
                const seqChar   = fname.charAt(fname.length - 1);

                if (uscore == '_' && this.isValidNumber(seqChar)) {
                    const seqNum = parseInt(seqChar, 10);
                    maxSeq = (maxSeq < seqNum) ? seqNum : maxSeq;
                }
            }
        });
        maxSeq++;
        const nextSeq = maxSeq;
        return this._data.editMaterialFileData.materialId + monographNum + '_' + nextSeq;
    }

    isValidNumber(test: string) {
        const num = parseInt(test, 10);
        return !isNaN(num);
    }

    // file editing
    onAddMonographFile() {
        this._validationMessage = '';

        const data = new MaterialFileData(0, this._data.material.materialId, '', '', MaterialFileType.MONOGRAPH, MaterialFileAction.ADD);
        this._data.editMaterialFileData = data;

        this._data.nextFileName = this.nextMonographFileName();
        const newMonographFile: MaterialMonograph = this._uow.materialMonographFactory.create();
        newMonographFile.materialId = this._data.material.materialId;

        this.entity = newMonographFile;
        this._data.edmMaterialMonograph.onlyEntity 	    = this.entity;
        this._data.edmMaterialMonograph.editing 		= true;

        this._data.inSubEditor = true;
    }

    onBack() {
        this._parentData.isEditingFiles = false;
        this._parent.refreshMonographStatus();
    }

    onCancel() {
        if (this._data.editMaterialFileData.fileType == MaterialFileType.MONOGRAPH) {
            this._data.edmMaterialMonograph.onCancelCore();
        }

        this._data.editMaterialFileData             = null;
        this._data.hasFileChanges                   = false;
        this._data.inSubEditor                      = false;
    }

    onConfirmRemove() {
        if (this._data.editMaterialFileData.fileAction != MaterialFileAction.DELETE) {
            return;
        }

        if (this._data.editMaterialFileData.fileType == MaterialFileType.MONOGRAPH) {
            this.removeMonographFile();
        }
    }

    // 03/09/2025: Editing not required yet
    onEdit(mf: MaterialFileData) {
        this._data.editMaterialFileData  =   mf;
        this._data.editMaterialFileData.fileAction = MaterialFileAction.EDIT;

        if (this._data.editMaterialFileData.fileType == MaterialFileType.MONOGRAPH) {
            this.fetchMaterialMonographForEdit(this._data.editMaterialFileData.key).then(r => {
                if (r) {
                    this._data.inSubEditor = true;
                }
            });
        }
    }

    onRemove(mf: MaterialFileData) {
        this._validationMessage = '';

        this._data.editMaterialFileData  =   mf;
        this._data.editMaterialFileData.fileAction = MaterialFileAction.DELETE;

        if (this._data.editMaterialFileData.fileType == MaterialFileType.MONOGRAPH) {
            this.fetchMaterialMonographForEdit(this._data.editMaterialFileData.key).then(r => {
                if (r) {
                    this._data.inSubEditor = true;
                }
            });
        }
    }

    onSave() {
        if (this._data.editMaterialFileData.fileType == MaterialFileType.MONOGRAPH) {
            if (this._data.editMaterialFileData.fileAction == MaterialFileAction.ADD) {
                this.saveMonographFile();
            }
        }
    }

    removeMonographFile() {
        this._data.edmMaterialMonograph.currentEntity.entityAspect.setDeleted();

        this.recordMaterialWorkflow(WorkflowEntityState.Deleted, 'Monograph file deleted');

        this._data.edmMaterialMonograph.onApplyCore();
        return this._data.edmMaterialMonograph.onSaveCore().then(() => {
            this.removeMaterialFile( this._data.editMaterialFileData.fileName, 'Monographs');
        }).catch((e) => {
            // leave in _editing mode to see error.
            this._data.edmMaterialMonograph.errorMessages.push(e.toString());
            this._validationMessage = e.toString();
        });
    }

    saveMonographFile() {
        this._data.editMaterialFileData.fileLabel   = this._data.edmMaterialMonograph.currentEntity.fileLabel;
        this._data.editMaterialFileData.fileName    = this._data.edmMaterialMonograph.currentEntity.fileName;

        this.recordMaterialWorkflow(WorkflowEntityState.Added, 'Monograph file added');

        return this._data.edmMaterialMonograph.onSaveCore().then(() => {
            this._data.edmMaterialMonograph.editing = false;
            this.uploadMaterialFile('Monographs');
        }).catch((e) => {
            this._data.edmMaterialMonograph.errorMessages.push(e.toString());
            this._validationMessage = e.toString();
        });
    }

    pageTitle() {
        if (!this._data.inSubEditor) {
            return 'Material File Manager';
        }

        let action = '';
        if (this._data.editMaterialFileData.fileAction == MaterialFileAction.ADD) {
            action = 'Adding';
        } else if (this._data.editMaterialFileData.fileAction == MaterialFileAction.DELETE) {
            action = 'Removing';
        } else if (this._data.editMaterialFileData.fileAction == MaterialFileAction.EDIT) {
            action = 'Renaming';
        }

        return (action + ' ' + this._data.editMaterialFileData.materialFileType() + ' File');
    }

    refreshMaterialData(materialId: number): Promise<any> {
        return this._uow.materialRepository.withId(materialId).then(m => {
            if (m) {
                this._data.material    = m;
                this._stateMap.currentMaterial = m;

                this._hasMonographFiles = (this._data.material.materialMonographs != null && this._data.material.materialMonographs.length > 0);

                this.prepareFileDisplay();
            } else {
                this._validationMessage = 'Error refreshing material data after file changes.';
            }
        });
    }

    recordMaterialFile(key: number, fileLabel: string, fileName: string, fileType: MaterialFileType, fileAction: MaterialFileAction, color: string) {
        const data = new MaterialFileData(key, this._data.material.materialId, fileLabel, fileName, fileType, fileAction);
        this._materialFilesEx.push(new SelectableEntity<MaterialFileData>(data, color, '', false));
    }

    filterToExpertPanelSummaryId(expertPanelID: number): ExpertPanelSummary {
        if (this._summaries == null || this._summaries.length < 1) {
            return null;
        }

        const eps = this._summaries.filter(e => e.expertPanelID == expertPanelID);
        if (eps != null && eps.length > 0) {
            return eps[0];
        }
    }

    fetchMaterialForEdit(materialId: number): Promise<any> {
        this._data.edmMaterialMonograph.editing = false;

        return this._uow.materialRepository.withId(materialId).then(m => {
            if (m) {
                this._data.material    				= m;
                this._stateMap.currentMaterial 	    = m;
                // this._data.edmMaterial.onlyEntity   = m; // for workflow
                // this._data.edmMaterial.editing      = true;
            }
        });
    }

    fetchMaterialMonographForEdit(materialMonographId: number): Promise<boolean> {
        this._data.edmMaterialMonograph.editing = false;

        let result = false;
        const param = {materialMonographId: materialMonographId};
        return this._uow.fetch('Materials/MaterialMonographsById', param).then(m => {
            if (m != null && m.length > 0) {
                this.entity = m[0];
                this._data.edmMaterialMonograph.onlyEntity 	    = this.entity;
                this._data.edmMaterialMonograph.editing 		= true;
                result = true;
            } else {
                this._validationMessage = 'Error retrieving data for editing.';
            }
            return Promise.resolve(result);
        });
    }

    isValidString(teststring: string) {
        if (!teststring) { return false; }

        return (teststring.trim().length > 0);
    }

    recordMaterialWorkflow(workflowState: WorkflowEntityState, notes: string) {
        const fileName  = this._data.edmMaterialMonograph.currentEntity.fileName;
        const key       = this._data.edmMaterialMonograph.currentEntity.materialMonographId;
        this._materialWorkflowService.recordMaterialMonographChange(this._data.material.materialId, workflowState, fileName, key, notes, this._userManager.currentUser.name);
    }

    // ************************************************************************
    // web api calls
    // ************************************************************************
    // add file
    getUploadFileURL(subFolder: string): Observable<any> {
        const url = this._documentManagementUrl + '/UploadFile';
        const headers = new HttpHeaders();
        headers.append('Accept', 'application/json');

        const formData: FormData = new FormData();
        formData.append('fileName', this._data.editMaterialFileData.fileName);
        formData.append('subFolder', subFolder);
        formData.append('file', this._data.editMaterialFileData.fileData);

        return this._httpClient.post<any>(url, formData);
    }

    public uploadMaterialFile(subFolder: string): Observable<string> {
        const subject = new Subject<string>();
        this.getUploadFileURL(subFolder)
            .subscribe(
                (response) => {
                    const data = response;

                    if (data.Success == false) {
                        subject.next('Error occurred: ' + data.ExceptionMessage);
                        this._validationMessage = 'Error occurred: ' + data.ExceptionMessage;
                    } else {
                        subject.next('File was processed successfully.');
                        this._validationMessage = 'File was uploaded successfully.';

                        this.refreshMaterialData(this._data.material.materialId);

                        this._data.hasFileChanges   = false;
                        this._data.inSubEditor      = false;
                    }
                },
                (error) => {
                    subject.next(error.message);
                });

        return subject.asObservable();
    }

    // remove file
    getRemoveMaterialFileURL(fileName: string, subFolder: string): Observable<any> {
        const url = this._documentManagementUrl + '/Remove?fileName=' + fileName + '&subFolder=' + subFolder;

        const headers = new HttpHeaders();
        headers.append('Accept', 'application/json');
        return this._httpClient.post<any>(url, { });
    }

    public removeMaterialFile(fileName: string, subFolder: string): Observable<string> {
        const subject = new Subject<string>();
        this._isLoading = true;

        this.getRemoveMaterialFileURL(fileName, subFolder)
            .subscribe(
                (response) => {
                    const data = response;

                    if (data.Success == false) {
                        this._validationMessage = 'Error occurred, unable to remove the file: ' + data.ExceptionMessage;
                        subject.next('Error occurred, unable to remove the file: ' + data.ExceptionMessage);
                        return;
                    }

                    this._validationMessage = 'File archived and removed successfully.';
                    subject.next('File archived and removed successfully.');

                    this.refreshMaterialData(this._data.material.materialId);

                    this._data.hasFileChanges   = false;
                    this._data.inSubEditor      = false;
                },
                (error) => {
                    subject.next(error.message);
                },
                () => {
                    this._isLoading = false;
                });

        return subject.asObservable();
    }
}
